/*
 * Cart drawer discount-code demo.
 *
 * Uses the native Cart AJAX API discount support (Shopify Summer '25):
 *   POST /cart/update.js  { "discount": "CODE1,CODE2", "sections": [...] }
 *
 * The `discount` param REPLACES the whole set of codes, so we always send the
 * union / difference of the codes currently on the cart. Validation feedback
 * comes from the response's `discount_codes` array: each entry is
 * { code, amount, applicable } — `applicable: false` means invalid or not
 * usable for this cart (Shopify returns the same for both to deter guessing).
 *
 * The theme patches window.fetch (see cart-487.js) and re-renders the drawer
 * from any cart response's `sections`, so the meter/totals refresh for free.
 */
if (!customElements.get('cart-discount-form')) {
  const escapeHtml = (str) =>
    String(str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[c]);

  // localStorage can throw in private mode / sandboxed iframes — never let that
  // break the form. Used to remember when a shopper removes the auto-applied code.
  const safeStorageGet = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };
  const safeStorageSet = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* ignore */
    }
  };

  class CartDiscountForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="discount"]');
      this.messageEl = this.querySelector('[data-discount-message]');
      this.codesEl = this.querySelector('[data-discount-codes]');
      this.sectionId = this.dataset.sectionId;

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        const code = (this.input.value || '').trim();
        if (code) this.applyCode(code);
      });

      // Delegated so re-rendered chips keep working.
      this.codesEl?.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-remove-discount]');
        if (btn) this.removeCode(btn.dataset.removeDiscount);
      });

      // Restore a message that was set just before a drawer re-render wiped us.
      const pending = window.__cartDiscountMessage;
      if (pending && Date.now() - pending.at < 4000) {
        this.showMessage(pending.text, pending.type);
      }

      this.autoApply();
    }

    get appliedCodes() {
      return Array.from(this.querySelectorAll('[data-remove-discount]')).map(
        (btn) => btn.dataset.removeDiscount
      );
    }

    async applyCode(code) {
      const set = Array.from(new Set([...this.appliedCodes, code]));
      const cart = await this.updateDiscounts(set);
      if (!cart) return;

      const entry = (cart.discount_codes || []).find(
        (d) => d.code.toLowerCase() === code.toLowerCase()
      );
      if (entry && entry.applicable) {
        this.input.value = '';
        this.setMessage(`“${code}” applied.`, 'success');
      } else {
        this.setMessage(`“${code}” isn’t valid for this cart.`, 'error');
      }
    }

    // Auto-apply the merchant-configured promo (data-auto-apply) at most once per
    // page load. Additive — it never clobbers codes the shopper typed in. If the
    // shopper removes it, we remember that and stop re-applying (see removeCode).
    autoApply() {
      const code = (this.dataset.autoApply || '').trim();
      if (!code) return;

      const key = code.toLowerCase();
      // Shopper removed it on a previous visit — respect that.
      if (safeStorageGet(`auto-discount:${key}`) === 'removed') return;
      // Already on the cart (e.g. rebuilt after a drawer re-render) — leave it.
      if (this.appliedCodes.some((c) => c.toLowerCase() === key)) return;

      // Attempt once per page load, shared across the drawer's two instances and
      // every re-render, so a not-yet-eligible code can't trigger an apply loop.
      window.__autoDiscountAttempted =
        window.__autoDiscountAttempted || new Set();
      if (window.__autoDiscountAttempted.has(key)) return;
      window.__autoDiscountAttempted.add(key);

      this.autoApplyCode(code);
    }

    async autoApplyCode(code) {
      const set = Array.from(new Set([...this.appliedCodes, code]));
      // Silent on failure: an unprompted "code isn't valid" message is confusing.
      // Shopify enforces the code's own conditions; if it isn't eligible yet, a
      // later page load will try again.
      await this.updateDiscounts(set);
    }

    async removeCode(code) {
      const set = this.appliedCodes.filter(
        (c) => c.toLowerCase() !== code.toLowerCase()
      );
      const cart = await this.updateDiscounts(set);
      if (!cart) return;
      this.setMessage(`“${code}” removed.`, 'success');

      // If they removed the auto-applied code, don't re-apply it next visit.
      const auto = (this.dataset.autoApply || '').trim().toLowerCase();
      if (auto && auto === code.toLowerCase()) {
        safeStorageSet(`auto-discount:${auto}`, 'removed');
      }
    }

    async updateDiscounts(codes) {
      this.classList.add('loading');
      try {
        const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            discount: codes.join(','),
            sections: this.sectionId ? [this.sectionId] : undefined,
          }),
        });
        if (!res.ok) {
          this.setMessage('Something went wrong. Please try again.', 'error');
          return null;
        }
        const cart = await res.json();
        this.renderChips(cart.discount_codes);
        return cart;
      } catch (err) {
        console.error('Discount update failed', err);
        this.setMessage('Network error. Please try again.', 'error');
        return null;
      } finally {
        this.classList.remove('loading');
      }
    }

    // Rebuild chips from the cart so mobile (footer isn't auto re-rendered) stays in sync.
    renderChips(discountCodes) {
      const applicable = (discountCodes || []).filter((d) => d.applicable);
      document.querySelectorAll('cart-discount-form [data-discount-codes]').forEach((el) => {
        el.innerHTML = applicable
          .map(
            (d) => `
              <li class="flex items-center gap-1 bg-[#e7d4c3] text-cta-brown text-[13px] px-2 py-1 rounded-sm">
                <span>${escapeHtml(d.code)}</span>
                <button type="button" data-remove-discount="${escapeHtml(d.code)}" aria-label="Remove discount ${escapeHtml(d.code)}" class="leading-none text-[16px]">&times;</button>
              </li>`
          )
          .join('');
      });
    }

    // Stash + broadcast so the message survives the drawer's section re-render.
    setMessage(text, type) {
      window.__cartDiscountMessage = { text, type, at: Date.now() };
      document.querySelectorAll('cart-discount-form').forEach((el) => {
        if (typeof el.showMessage === 'function') el.showMessage(text, type);
      });
    }

    showMessage(text, type) {
      if (!this.messageEl) return;
      this.messageEl.textContent = text;
      this.messageEl.classList.remove('hidden');
      this.messageEl.style.color = type === 'error' ? '#b00020' : '#2e7d32';
    }
  }

  customElements.define('cart-discount-form', CartDiscountForm);
}
